import React, { useState, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import styles from './TabAvisGoogle.module.css';
import MetaHead from '../components/MetaHead';
import { normalizeTimestamp } from '../utils/normalizeTimestamp';
import { formatDate } from '../utils/format';

/* ============================================================
   TabAvisGoogle — onglet "Avis Google"
   ------------------------------------------------------------
   IDs DOM préservés pour le backend Express :
     #ag-sync-btn       (bouton "Synchroniser tous les avis")
     #ag-total-label    (compteur d'avis)
     #ag-source-badge   (badge live/cache)
     #ag-sort-select    (select tri)
     #ag-list           (conteneur de la liste)
     #ag-load-more      (zone "Voir plus")

   Comportements préservés (callbacks fournis en props) :
     - onSync()                     ← syncAllGoogleReviews()
     - onSortChange(value)          ← loadAvisGoogleSorted(value)
     - onLoadMore()                 ← loadMoreAvisGoogle()
     - onRefresh()                  ← syncGoogleLive()

   i18n P5b : useTranslation('tabs') + MetaHead page="avis_google".
   timeAgo utilise formatDate(date, lang) pour la fallback "DD mmm YYYY".
   Note : le filtre "langue" est la langue de l'AVIS Google (review.language
   détectée par Google), pas la langue du dashboard. Aucun filtre langue
   n'est ajouté ici en P5b — il n'existait pas avant P5a non plus.
   ============================================================ */

/* ----------------------------------------------------------
   Helpers
---------------------------------------------------------- */
function initialsFor(name = '') {
  return name
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase() || '')
    .join('') || '?';
}

function ratingBucket(stars = 0) {
  if (stars >= 4) return 'positive';
  if (stars === 3) return 'neutral';
  return 'negative';
}

function buildTimeAgo(t, lang) {
  return function timeAgo(value) {
    const d = normalizeTimestamp(value);
    if (!d) return '';
    const diff = (Date.now() - d.getTime()) / 1000;
    if (diff < 60)        return t('avis_google.time_just_now');
    if (diff < 3600)      return t('avis_google.time_minutes_ago', { count: Math.floor(diff / 60) });
    if (diff < 86400)     return t('avis_google.time_hours_ago', { count: Math.floor(diff / 3600) });
    if (diff < 86400 * 7) return t('avis_google.time_days_ago', { count: Math.floor(diff / 86400) });
    return formatDate(d, lang, { day: 'numeric', month: 'short', year: 'numeric' });
  };
}

function isOverdue(review) {
  if (review.replied) return false;
  const d = normalizeTimestamp(review.date);
  if (!d) return false;
  return (Date.now() - d.getTime()) > 5 * 86400 * 1000;
}

/* ----------------------------------------------------------
   Stars
---------------------------------------------------------- */
function Stars({ value = 0, t }) {
  const v = Math.max(0, Math.min(5, Math.round(value)));
  return (
    <div className={styles.rating}>
      <span className={styles.stars} aria-label={t('avis_google.stars_aria', { count: v })}>
        <span className={styles.starOn}>{'★'.repeat(v)}</span>
        <span className={styles.starOff}>{'★'.repeat(5 - v)}</span>
      </span>
      <span className={styles.ratingNum}>{value.toFixed?.(1) ?? v}</span>
    </div>
  );
}

/* ----------------------------------------------------------
   ReviewCard
---------------------------------------------------------- */
function ReviewCard({ review, onReply, onView, t, timeAgo }) {
  const [expanded, setExpanded] = useState(false);
  const bucket = ratingBucket(review.rating);
  const overdue = isOverdue(review);
  const longText = (review.text || '').length > 240;
  const displayText = expanded || !longText
    ? review.text
    : `${(review.text || '').slice(0, 240)}…`;

  const variantClass =
    bucket === 'positive' ? styles.reviewPositive
    : bucket === 'neutral' ? styles.reviewNeutral
    : styles.reviewNegative;

  return (
    <article
      className={`${styles.review} ${variantClass}`}
      data-review-id={review.id}
      data-rating={review.rating}
    >
      <header className={styles.reviewHead}>
        <div className={styles.author}>
          <div className={styles.avatar}>{initialsFor(review.author)}</div>
          <div className={styles.authorMeta}>
            <div className={styles.authorName}>{review.author || t('avis_google.anonymous_client')}</div>
            <div className={styles.authorSub}>
              <span>{timeAgo(review.date)}</span>
              {review.source && (
                <>
                  <span className={styles.dot}>·</span>
                  <span>{review.source}</span>
                </>
              )}
              {review.platform && (
                <>
                  <span className={styles.dot}>·</span>
                  <span>{review.platform}</span>
                </>
              )}
            </div>
          </div>
        </div>
        <Stars value={review.rating || 0} t={t} />
      </header>

      {review.text && (
        <p className={styles.reviewText}>{displayText}</p>
      )}
      {longText && (
        <button
          type="button"
          className={styles.expandBtn}
          onClick={() => setExpanded((e) => !e)}
        >
          {expanded ? t('avis_google.expand_hide') : t('avis_google.expand_show')}
        </button>
      )}

      {Array.isArray(review.tags) && review.tags.length > 0 && (
        <div className={styles.tags}>
          {review.tags.map((tag, i) => (
            <span
              key={i}
              className={`${styles.tag} ${
                tag.tone === 'positive' ? styles.tagPositive
                : tag.tone === 'negative' ? styles.tagNegative
                : ''
              }`}
            >
              {tag.label || tag}
            </span>
          ))}
        </div>
      )}

      {review.reply && (
        <div className={styles.reply}>
          <div className={styles.replyHead}>
            <span className={styles.replyOwner}>{t('avis_google.reply_owner_label')}</span>
            {review.reply.date && (
              <span className={styles.replyDate}>{timeAgo(review.reply.date)}</span>
            )}
          </div>
          <p className={styles.replyText}>{review.reply.text}</p>
        </div>
      )}

      <footer className={styles.reviewFoot}>
        {review.replied ? (
          <span className={`${styles.statusPill} ${styles.statusReplied}`}>
            {t('avis_google.status_replied')}
          </span>
        ) : overdue ? (
          <span className={`${styles.statusPill} ${styles.statusOverdue}`}>
            {t('avis_google.status_overdue')}
          </span>
        ) : (
          <span className={`${styles.statusPill} ${styles.statusPending}`}>
            {t('avis_google.status_pending')}
          </span>
        )}
        <div style={{ display: 'flex', gap: 14 }}>
          {!review.replied && onReply && (
            <button
              type="button"
              className={styles.actionLink}
              onClick={() => onReply(review)}
            >
              {t('avis_google.btn_reply')}
            </button>
          )}
          {onView && (
            <button
              type="button"
              className={styles.actionLink}
              onClick={() => onView(review)}
            >
              {t('avis_google.btn_view_google')}
            </button>
          )}
        </div>
      </footer>
    </article>
  );
}

/* ----------------------------------------------------------
   Skeleton, Empty, Error
---------------------------------------------------------- */
function ReviewSkeleton() {
  return (
    <div className={styles.skeleton} aria-hidden="true">
      <div className={styles.skHead}>
        <div className={styles.skAvatar} />
        <div className={styles.skMeta}>
          <div className={`${styles.skLine} ${styles.skLineW40}`} />
          <div className={`${styles.skLine} ${styles.skLineW70}`} />
        </div>
      </div>
      <div className={`${styles.skLine} ${styles.skLineFull}`} style={{ marginBottom: 8 }} />
      <div className={`${styles.skLine} ${styles.skLineFull}`} style={{ marginBottom: 8, width: '85%' }} />
      <div className={`${styles.skLine} ${styles.skLineW70}`} />
    </div>
  );
}

function EmptyState({ onSync, t }) {
  return (
    <div className={styles.empty}>
      <div className={styles.emptyEmoji}>🍽️</div>
      <h3 className={styles.emptyTitle}>{t('avis_google.empty_title')}</h3>
      <p className={styles.emptyText}>{t('avis_google.empty_text')}</p>
      <button
        type="button"
        id="ag-sync-btn"
        className={styles.btnPrimary}
        onClick={onSync}
      >
        {t('avis_google.empty_btn_sync')}
      </button>
    </div>
  );
}

function ErrorState({ message, onRetry, t }) {
  return (
    <div className={styles.error} role="alert">
      <div className={styles.errorIcon}>!</div>
      <div className={styles.errorBody}>
        <p className={styles.errorTitle}>{t('avis_google.error_title')}</p>
        <p className={styles.errorText}>
          {message || t('avis_google.error_text_default')}
        </p>
        {onRetry && (
          <button type="button" className={styles.btnGhost} onClick={onRetry}>
            {t('avis_google.btn_retry')}
          </button>
        )}
      </div>
    </div>
  );
}

/* ----------------------------------------------------------
   TabAvisGoogle
---------------------------------------------------------- */
export default function TabAvisGoogle({
  reviews = [],
  total,
  googleTotal = null,
  source = null,
  loading = false,
  syncing = false,
  error = null,

  hasMore = false,
  loadingMore = false,

  onSync,
  onRefresh,
  onSortChange,
  onLoadMore,
  onReply,
  onViewOnGoogle,
  onRetry,

  initialSort = 'newest',
  initialFilter = 'all',
}) {
  const { t, i18n } = useTranslation('tabs');
  const lang = i18n.language;
  const [sort, setSort] = useState(initialSort);
  const [filter, setFilter] = useState(initialFilter);

  const timeAgo = useMemo(() => buildTimeAgo(t, lang), [t, lang]);

  const SORT_OPTIONS = [
    { value: 'newest', label: t('avis_google.sort_newest') },
    { value: 'oldest', label: t('avis_google.sort_oldest') },
    { value: 'best',   label: t('avis_google.sort_best') },
    { value: 'worst',  label: t('avis_google.sort_worst') },
  ];

  const QUICK_FILTERS = [
    { value: 'all',     label: t('avis_google.filter_all'),     emoji: '📋' },
    { value: 'pending', label: t('avis_google.filter_pending'), emoji: '⏳' },
    { value: '5stars',  label: t('avis_google.filter_5stars'),  emoji: '⭐' },
    { value: 'low',     label: t('avis_google.filter_low'),     emoji: '⚠️' },
  ];

  const handleSortChange = useCallback((e) => {
    const v = e.target.value;
    setSort(v);
    onSortChange && onSortChange(v);
  }, [onSortChange]);

  /* Filtrage local (pour les chips) — n'altère pas le tri serveur */
  const filteredReviews = useMemo(() => {
    if (filter === 'all') return reviews;
    if (filter === 'pending') return reviews.filter((r) => !r.replied);
    if (filter === '5stars')  return reviews.filter((r) => r.rating === 5);
    if (filter === 'low')     return reviews.filter((r) => (r.rating || 0) <= 3);
    return reviews;
  }, [reviews, filter]);

  /* Compteurs pour les chips */
  const counts = useMemo(() => ({
    all:     reviews.length,
    pending: reviews.filter((r) => !r.replied).length,
    '5stars': reviews.filter((r) => r.rating === 5).length,
    low:     reviews.filter((r) => (r.rating || 0) <= 3).length,
  }), [reviews]);

  const totalDisplay = total ?? reviews.length;

  /* ---------- Render ---------- */
  return (
    <>
      <MetaHead page="avis_google" />
      {/* Topbar locale */}
      <header className={styles.tabHeader}>
        <div className={styles.titleBlock}>
          <h1>
            <span className={styles.titleEmoji}>{t('avis_google.title_emoji')}</span>
            {t('avis_google.title')}
          </h1>
          <p>{t('avis_google.subtitle')}</p>
        </div>
        <div className={styles.actions}>
          <button
            type="button"
            className={styles.btnGhost}
            onClick={onRefresh}
            disabled={loading || syncing}
          >
            {t('avis_google.btn_refresh')}
          </button>
          <button
            type="button"
            id="ag-sync-btn"
            className={styles.btnPrimary}
            onClick={onSync}
            disabled={syncing}
          >
            <span className={syncing ? styles.spinner : ''}>🔄</span>
            {syncing ? t('avis_google.btn_sync_loading') : t('avis_google.btn_sync')}
          </button>
        </div>
      </header>

      {/* Erreur prioritaire */}
      {error && <ErrorState message={error} onRetry={onRetry} t={t} />}

      {/* Bandeau de stats (toujours rendu pour préserver les IDs) */}
      <div className={styles.statBar}>
        <div className={styles.statBarLeft}>
          <span id="ag-total-label" className={styles.totalLabel}>
            {loading ? (
              t('avis_google.total_label_loading')
            ) : googleTotal != null && Number(googleTotal) > totalDisplay ? (
              <>
                <strong>{totalDisplay}</strong>
                {t('avis_google.total_label_analyzed_mid')}
                <strong>{googleTotal}</strong>
              </>
            ) : (
              <>
                <strong>{totalDisplay}</strong>
                {t('avis_google.total_label_default_suffix')}
              </>
            )}
          </span>
          <span
            id="ag-source-badge"
            className={`${styles.sourceBadge} ${
              source === 'live' ? styles.sourceBadgeLive
              : source === 'cache' ? styles.sourceBadgeCache
              : ''
            }`}
            style={{ display: source ? 'inline-block' : 'none' }}
          >
            {source === 'live'  && t('avis_google.source_live')}
            {source === 'cache' && t('avis_google.source_cache')}
          </span>
        </div>

        <select
          id="ag-sort-select"
          className={styles.sortSelect}
          value={sort}
          onChange={handleSortChange}
          disabled={loading}
        >
          {SORT_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>
      </div>

      {/* Filtres rapides (chips) */}
      {!loading && reviews.length > 0 && (
        <div className={styles.filters}>
          {QUICK_FILTERS.map((f) => (
            <button
              key={f.value}
              type="button"
              className={`${styles.filterChip} ${
                filter === f.value ? styles.filterChipActive : ''
              }`}
              onClick={() => setFilter(f.value)}
            >
              <span>{f.emoji}</span>
              {f.label}
              <span className={styles.chipCount}>{counts[f.value] ?? 0}</span>
            </button>
          ))}
        </div>
      )}

      {/* Liste */}
      <div id="ag-list" className={styles.list}>
        {loading && reviews.length === 0 && (
          <>
            <ReviewSkeleton />
            <ReviewSkeleton />
            <ReviewSkeleton />
          </>
        )}

        {!loading && !error && reviews.length === 0 && (
          <EmptyState onSync={onSync} t={t} />
        )}

        {!loading && filteredReviews.map((r) => (
          <ReviewCard
            key={r.id ?? r.reviewId ?? `${r.author}-${r.date}`}
            review={r}
            onReply={onReply}
            onView={onViewOnGoogle}
            t={t}
            timeAgo={timeAgo}
          />
        ))}

        {!loading && reviews.length > 0 && filteredReviews.length === 0 && (
          <div className={styles.empty}>
            <div className={styles.emptyEmoji}>🔍</div>
            <h3 className={styles.emptyTitle}>{t('avis_google.empty_filter_title')}</h3>
            <p className={styles.emptyText}>{t('avis_google.empty_filter_text')}</p>
          </div>
        )}
      </div>

      {/* Load more */}
      <div
        id="ag-load-more"
        className={styles.loadMore}
        style={{ display: hasMore ? 'flex' : 'none' }}
      >
        <button
          type="button"
          className={styles.loadMoreBtn}
          onClick={onLoadMore}
          disabled={loadingMore}
        >
          {loadingMore ? t('avis_google.load_more_loading') : t('avis_google.load_more')}
        </button>
      </div>
    </>
  );
}
